import React from 'react';
import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';

import { connect } from 'react-redux';
import { DropdownItem, DropdownMenu, DropdownToggle, Nav, NavItem, UncontrolledDropdown } from 'reactstrap';
import { FormattedMessage, injectIntl } from 'react-intl';

import { capitalizeFirstLetter } from '../../../../util/text.js';

const CONVERSATIONS_LIST_PATHS = ['/', '/conversations'];

const normalizeMenuPath = path => (path || '').replace(/\/$/, '') || '/';

const isNavRouteActive = (routePath, pathname) => {
    if(!routePath || /^https?:\/\//.test(routePath)) {
        return false;
    }

    const route = normalizeMenuPath(routePath);
    const current = normalizeMenuPath(pathname);

    if(route === current) {
        return true;
    }

    return CONVERSATIONS_LIST_PATHS.includes(route) && CONVERSATIONS_LIST_PATHS.includes(current);
};

/**
 * App header menu (logged-in).
 * Links are defined in code with i18n keys so every enabled locale works
 * without a CMS panel translation (ja/ko had none for slug "menu").
 */
function PanelMainMenu({ intl, user = null, closeMenu = null }) {
    const location = useLocation();

    const isRouteAllowed = route => (
        !route.allowedRoles
        || (user?.role && route.allowedRoles.includes(user.role))
    );

    const handleNavClick = routeOnClick => event => {
        closeMenu?.();
        routeOnClick?.(event);
    };

    const getRoutes = () => ([
        {
            to: '/',
            name: 'conversationsMy',
        },
        {
            to: '/orders',
            name: 'appShopOrderHistory',
        },
        {
            to: '/user/profile',
            name: 'userAccountMy',
        },
        {
            name: 'console',
            allowedRoles: ['admin', 'manager', 'printer'],
            children: [
                {
                    to: '/console',
                    name: 'dashboard',
                    allowedRoles: ['admin'],
                },
                {
                    to: '/console/conversations',
                    name: 'conversations',
                    allowedRoles: ['admin', 'manager', 'printer'],
                },
                {
                    to: '/console/orders/onpreparation',
                    name: 'orders',
                    allowedRoles: ['admin', 'manager', 'printer'],
                },
                {
                    to: '/console/users',
                    name: 'users',
                    allowedRoles: ['admin', 'manager'],
                },
                {
                    to: '/console/products',
                    name: 'products',
                    allowedRoles: ['admin'],
                },
                {
                    to: '/console/vouchers',
                    name: 'vouchers',
                    allowedRoles: ['admin', 'manager'],
                },
                {
                    to: '/console/shippings',
                    name: 'shippings',
                    allowedRoles: ['admin'],
                },
                {
                    to: '/console/vat',
                    name: 'vatRates',
                    allowedRoles: ['admin'],
                },
                {
                    to: '/console/vat/report',
                    name: 'vatReport',
                    allowedRoles: ['admin'],
                },
                {
                    to: '/console/config',
                    name: 'configuration',
                    allowedRoles: ['admin'],
                },
            ],
        },
    ]);

    const renderRoutes = (routes, level = 0) => routes
        .filter(route => route && isRouteAllowed(route))
        .map((route, index) => {
            if(route.type === 'divider') {
                return <NavItem key={index} className="p-2 sidebar-divider text-dark">|</NavItem>;
            }
            if(route.type === 'title') {
                return (
                    <NavItem key={index} className="d-block sidebar-heading">
                        <span className="p-2" style={{ position: 'absolute', top: '0' }}>
                            <FormattedMessage id={route.name} defaultMessage={capitalizeFirstLetter(route.name)} />
                        </span>
                    </NavItem>
                );
            }
            if((route.children || []).length) {
                return (
                    <UncontrolledDropdown key={index} nav inNavbar>
                        <DropdownToggle nav caret>
                            <span className="menuText p-1 pt-3">
                                <FormattedMessage id={route.name} defaultMessage={capitalizeFirstLetter(route.name)} />
                            </span>
                        </DropdownToggle>
                        <DropdownMenu className="general-submenu">
                            {renderRoutes(route.children, level + 1)}
                        </DropdownMenu>
                    </UncontrolledDropdown>
                );
            }

            const label = intl.formatMessage({
                id: route.name,
                defaultMessage: capitalizeFirstLetter(route.name),
            });
            const isActive = isNavRouteActive(route.to, location.pathname);
            const menuTextClassName = `menuText p-1${isActive ? ' text-primary' : ''}`;
            const navLink = route.isExternal
                ? (
                    <a href={route.to} target="_blank" className="nav-link" rel="noreferrer" onClick={handleNavClick(route.onClick)}>
                        <span className={menuTextClassName}>{label}</span>
                    </a>
                ) : (
                    <Link to={route.to} onClick={handleNavClick(route.onClick)} className="nav-link">
                        <span className={menuTextClassName}>{label}</span>
                    </Link>
                );

            return level ? (
                <DropdownItem key={index}>{navLink}</DropdownItem>
            ) : (
                <NavItem key={index} tag="li">
                    {navLink}
                </NavItem>
            );
        });

    if(!user) {
        return null;
    }

    return (
        <Nav id="accordionSidebar" navbar>
            {renderRoutes(getRoutes())}
        </Nav>
    );
}

function mapStateToProps(store) {
    return {
        user: store.users.user,
    };
}

PanelMainMenu.propTypes = {
    intl: PropTypes.object.isRequired,
    user: PropTypes.object,
    // Kept optional: CMS model viewer may still pass a panel; app menu ignores it.
    panel: PropTypes.object,
    isMobile: PropTypes.bool,
    closeMenu: PropTypes.func,
};

export default connect(mapStateToProps)(injectIntl(PanelMainMenu));
